import { faStickyNote } from "@fortawesome/pro-solid-svg-icons";
import { Tooltip } from "@growtherapy/sprout-ui";
import { Popover, PopoverButton } from "@headlessui/react";
import {
  ComponentProps,
  FC,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { ControlPanelButton } from "../../components";
import { PopoverChevronIndicator } from "../../components/popover/PopoverChevronIndicator";
import { PopoverPanel } from "../../components/popover/PopoverPanel";
import { useKeyPressEvent } from "../../hooks/useKeyPressEvent";
import { capitalize, mergeRefs } from "../../utils";
import { InSessionPatientNoteWrapper as InSessionPatientNote } from "../patient-note";
import { ErrorBoundary } from "react-error-boundary";
import { logger } from "../../datadog";
import { toast } from "../../toasts";

const PatientNotePopoverButton = forwardRef<
  HTMLButtonElement,
  { isOpen: boolean }
>(function PatientNotePopoverButton({ isOpen, ...props }, ref) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const mergedRefs = useMemo(
    () => mergeRefs<HTMLButtonElement>(ref, buttonRef),
    [ref, buttonRef],
  );

  /**
   * Unfortunately there's no way to programmatically open and close the
   * popover. Our workaround is to "click" the button to toggle it.
   */
  const togglePopover = useCallback(() => {
    buttonRef.current?.click();
  }, []);

  useKeyPressEvent("N", togglePopover);

  const actionName = isOpen ? "close" : "open";

  return (
    <ControlPanelButton
      {...props}
      ref={mergedRefs}
      aria-label={`${capitalize(actionName)} sticky note. Pressing shift + n will also ${actionName} the sticky note.`}
      iconDefinition={faStickyNote}
    >
      <PopoverChevronIndicator isOpen={isOpen} />
    </ControlPanelButton>
  );
});

export type PatientNotePopoverControlButtonProps = {
  patientNoteComponent: FC<ComponentProps<typeof InSessionPatientNote>>;
};

function PatientNotePopoverErrorFallback() {
  useEffect(() => {
    logger.error("PatientNotePopoverControlButton threw an unexpected error");
    toast.error(
      "An unexpected error occurred while attempting to open the sticky note.",
    );
  }, []);
  return null;
}

export function PatientNotePopoverControlButton({
  patientNoteComponent: PatientNoteComponent,
}: PatientNotePopoverControlButtonProps) {
  return (
    <ErrorBoundary FallbackComponent={PatientNotePopoverErrorFallback}>
      <Popover>
        {({ open }: { open: boolean }) => (
          <>
            <Tooltip
              text={`${open ? "Close" : "Open"} sticky note (shift + n)`}
              childIsInteractive
            >
              <PopoverButton as={PatientNotePopoverButton} isOpen={open} />
            </Tooltip>
            <PopoverPanel
              anchor={undefined}
              className="min-w-full sm:min-w-[570px] !max-w-[570px]"
            >
              <PatientNoteComponent />
            </PopoverPanel>
          </>
        )}
      </Popover>
    </ErrorBoundary>
  );
}

export function PatientNotePopoverControlButtonWrapper() {
  return (
    <PatientNotePopoverControlButton
      patientNoteComponent={InSessionPatientNote}
    />
  );
}
